import { lazy, Suspense } from 'react'

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'

import { ArrowLeftIcon } from 'lucide-react'

import { LoadFailureNotice } from '@/features/custom-dashboards/components/load-failure-notice'
import { useDashboardConfig } from '@/features/custom-dashboards/persistence-queries'
import { resolveActiveDashboard } from '@/features/custom-dashboards/resolve-active-dashboard'
import { usePinnedDashboardIds } from '@/features/custom-dashboards/use-pinned-dashboards'

import { ROLES } from '@/shared/auth/constants'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

// The Builder pulls the whole authoring form + live-preview query stack (and, through
// it, the chart libs) — code-split it so the dashboard surface doesn't pay for the
// builder until the user navigates here (P-21). Named export → re-map to default.
const WidgetBuilder = lazy(() =>
	import('@/features/custom-dashboards/components/builder/widget-builder').then(
		(m) => ({ default: m.WidgetBuilder }),
	),
)

/**
 * Full-page Widget Builder (PostHog-style: form left, live Preview right). Reached from
 * the dashboard surface's "New widget" action (carries `?dashboard=` as the save target)
 * or the Widget Library's "edit" (carries `?widget=` to load an existing widget).
 *
 * Analyst+ where the rest of the surface is Read Only+: authoring a widget is a
 * `ManageDashboards` write from the first keystroke, so there is nothing here for a
 * Read Only user to read. Nothing links them here, and a deep link bounces to
 * /unauthorized rather than to a form whose Save always 403s. See the parent route
 * for the permission table.
 *
 * Gated on the dashboards document resolving before mounting the Builder: the builder's
 * form state is seeded ONCE from `initialWidget`, so in edit mode the widget must be in
 * hand before render. A failed load is its own state — waiting forever on a skeleton is
 * the blank screen P-17 forbids.
 */
export const Route = createFileRoute('/_dashboard/custom-dashboards/builder')({
	beforeLoad: () => roleAuthGuard(ROLES.Analyst),
	component: WidgetBuilderRoute,
})

function WidgetBuilderRoute() {
	const navigate = useNavigate()
	const search = Route.useSearch()
	const { data: config, isError, error, refetch } = useDashboardConfig()
	// Above the early returns below — the save target's fallback chain now has a
	// pin rung, and a hook cannot be called after a conditional return.
	const pinnedDashboardIds = usePinnedDashboardIds()

	// Nothing can be authored, let alone saved, without the document the Builder is
	// seeded from — so a failed read replaces the form rather than waiting forever on
	// a skeleton (P-17).
	if (isError) {
		return (
			<div className="flex h-full flex-col">
				<LoadFailureNotice
					error={error}
					subject="your widgets and dashboards"
					onRetry={() => void refetch()}
					action={
						<Button
							variant="ghost"
							nativeButton={false}
							render={<Link to="/custom-dashboards" />}
						>
							Back to dashboards
						</Button>
					}
				/>
			</div>
		)
	}
	if (!config) return <BuilderRouteSkeleton />

	const dashboards = Object.values(config.dashboards)
	const initialWidget = search.widget
		? config.widgets[search.widget]
		: undefined

	// "Save & add" must default to the dashboard the user is working on, and with
	// no `?dashboard=` (Library "New widget", sidebar deep-links) that is their
	// first pin, then the tenant default — the same chain the index route and
	// sidebar answer with, under one owner. A user who lands on their pin
	// and hits "New widget" from the Library must not have the save silently
	// aimed at the Tenant's default instead. Without this, the bar fell back to
	// `dashboards[0]`, an arbitrary `Object.values` slot (observed live on
	// 2026-08-23).
	const saveTarget = resolveActiveDashboard(dashboards, {
		urlDashboardId: search.dashboard,
		pinnedDashboardIds,
		defaultDashboardId: config.defaultDashboardId,
	})?.id

	const onSaved = (dashboardId?: string) =>
		dashboardId
			? navigate({
					to: '/custom-dashboards',
					search: (prev) => ({
						...prev,
						dashboard: dashboardId,
						widget: undefined,
					}),
				})
			: // Save-to-library (no target dashboard) lands on the widget the user
				// just saved — the Library's Widgets TAB, not its dashboards resting
				// state.
				navigate({
					to: '/custom-dashboards/library',
					search: { tab: 'widgets' },
				})

	return (
		<div className="flex h-full w-full flex-col">
			{/* Routing decision, not UI composition (A-01: routes import, they don't
			    build) — where "back" lands depends on the raw `?dashboard=` /
			    `?widget=` this component already has in hand for `onSaved` above, so
			    computing it a second time inside the Builder (or threading it through
			    VizFirstHeader as a prop) would duplicate that logic rather than reuse
			    it. See `BuilderBackLink` for the two destinations and why a deep link
			    with neither param still resolves to one. */}
			<div className="shrink-0 border-b px-6 py-3">
				<BuilderBackLink dashboardId={search.dashboard} />
			</div>
			<div className="min-h-0 flex-1">
				{/* P-19: the Builder is this route's single data section, so one boundary
				    around it is the whole rule. Without it a render error in the authoring
				    form white-screens the page instead of offering a retry. */}
				<ErrorBoundary
					feature="custom-dashboards"
					section="widget-builder"
					entityId={search.widget}
				>
					<Suspense fallback={<BuilderRouteSkeleton />}>
						<WidgetBuilder
							initialWidget={initialWidget}
							dashboards={dashboards}
							targetDashboardId={saveTarget}
							onSaved={onSaved}
						/>
					</Suspense>
				</ErrorBoundary>
			</div>
		</div>
	)
}

/**
 * The success path's only way out besides Save. Two entry points reach the Builder
 * (see the route doc above) and neither leaves a browser-history entry the user can
 * trust: "Configure" replaces `search` wholesale (`widget-menu.tsx`, so `?dashboard=`
 * never survives it even when the tile sits on a dashboard) and the Library's "New
 * widget" carries no param at all. Router history (`navigate(-1)`) would dead-end a
 * DEEP LINK — there is no entry to pop to — so this resolves an explicit target from
 * `?dashboard=` instead, the same fork `onSaved` already answers with above: present
 * ⇒ back to that dashboard, absent ⇒ back to the Library's Widgets tab, which is
 * where BOTH an edit-mode deep link (`?widget=`, no `?dashboard=`) and a truly bare
 * deep link resolve — never a dead end either way.
 */
export function BuilderBackLink({ dashboardId }: { dashboardId?: string }) {
	return dashboardId ? (
		<Button
			variant="outline"
			nativeButton={false}
			render={
				<Link to="/custom-dashboards" search={{ dashboard: dashboardId }} />
			}
		>
			<ArrowLeftIcon />
			Back to dashboards
		</Button>
	) : (
		<Button
			variant="outline"
			nativeButton={false}
			render={
				<Link to="/custom-dashboards/library" search={{ tab: 'widgets' }} />
			}
		>
			<ArrowLeftIcon />
			Back to library
		</Button>
	)
}

/** Shape-matched fallback for both the document wait and the lazy-chunk load — mirrors
 * the Builder's two-column form/preview layout. */
function BuilderRouteSkeleton() {
	return (
		<div className="grid h-full grid-cols-1 gap-6 p-6 lg:grid-cols-2">
			<Skeleton className="h-full w-full" />
			<Skeleton className="h-full w-full" />
		</div>
	)
}
